/********************************************************************************
Filename: np-select.js
Created: 07 Apr 2021
Author: Ron Robertson
Description: A stylized select replacement
Usage Example:
<np-select placeholder='Select an option'>
    <np-select-option value='1'>Option One</np-select-option>
    <np-select-option value='2' selected>Option Two</np-select-option>
    <np-select-option value='3'>Option Three</np-select-option>
    <np-select-option value='4'>Option Four</np-select-option>
    <np-select-option value='5'>Option Five</np-select-option>
</np-select>
********************************************************************************/

(function() {
    const template = document.createElement('template');

    template.innerHTML = `
        <style>
            #np-select{

                position: relative;
            }
            #labelWrapper{
                padding-right:15px;
                display:block;
            }
            #labelContent{
                display:inline-block;
                clear:both;
                line-height:24px;
                color: var(--labelColor, rgba(0,0,0,0.6));
            }
            #np-select .buttonWrapper{
                display: flex;
                justify-content:center;
                align-items:center;
                box-sizing:border-box;
                border-radius:3px;
                border:1px solid #a8a8a8;
                box-shadow: inset 0px 1px 3px 0px rgba(0, 0, 0, 0.27);
                color:rgba(0, 0, 0, 0.95);
                padding:0 9px;
                height:36px;
                width:100%;
                margin:5px 0 25px;
            }
            button:hover{
                cursor:pointer;
            }
            .selectedOption {
                display: block;
                width: 100%;
                border: 0px;
                outline: none;
                background-color: transparent;
                text-align: left;
                white-space: nowrap;
            }
            .placeholder{
                color:#A2A2A2;
            }
            .toggleOptions {
                border: 0px;
                background-color: transparent;
                outline: none;
                display: flex;
                align-items: center;
                transition: all 0.3s ease;
                justify-self:end;
                border-radius:0px 0px 8px 8px;
                width:30px;
            }

            .toggleOptions svg {
                width: 20px;
                height: 20px;
                margin:0 auto;
            }

            .toggleOptions svg path {
                stroke-width: 0;
                stroke: none;
                fill: #000000;
            }

            #np-select .optionsWrapper {
                display: none;
                position: absolute;
                box-sizing:border-box;
                top: 100%;
                left: 0;
                right: 0;
                margin-top: -1px;
                margin-left: 0px;
                margin-right: 0px;
                z-index: 200;
                padding-top: 5px;
                background-color: #ffffff;
                border-radius: 0px 0px 3px 3px;
                border-left: 1px solid #a8a8a8;
                border-right: 1px solid #a8a8a8;
                border-bottom: 1px solid #a8a8a8;
                box-shadow: inset 0px 0px 3px 0px rgba(0, 0, 0, 0.27);
            }

            #np-select.open .buttonWrapper .toggleOptions{
                transform: scaleY(-1);
            }
            #np-select.open .optionsWrapper{
                display: block;
            }

            .optionsWrapper .options {
                    list-style: none;
                    margin: 0;
                    padding: 0;
                    text-align: left;
            }

            /* grayBox theme */
            #np-select.grayBox{
                border:none;
            }
            #np-select.grayBox .buttonWrapper{
                display:flex;
                flex-direction:column;
                border:none;
                padding:0;
            }
            #np-select.grayBox .buttonWrapper button.selectedOption{
                font-size: var(--bodyFontSize);
                padding:10px 0px 5px;
                background-color:#f2f2f2;
                border-radius:8px 8px 0px 0px;
                text-align:center;
                display:flex;
                flex-direction:column;
                justify-content:center;
                align-items:center;
            }
            #np-select.grayBox .buttonWrapper button.selectedOption .top{
                display:block;
                text-align:center;
                font-family: var(--h2FontFamily);
            }
            #np-select.grayBox .buttonWrapper button.selectedOption .btm{
                display:block;
                text-align:center;
                font-family: var(--h3FontFamily);
            }
            #np-select.grayBox .buttonWrapper button.toggleOptions{
                width:100%;
                background-color:#e6e6e6;
            }
            #np-select.grayBox .optionsWrapper{
                border-color:#e6e6e6;
                margin-top: 18px;
            }
            /* END grayBox theme */

        </style>

        <div id="np-select">
            <label id="labelWrapper>
                <label id="errorLabel" class='error'></label>
                <span id="labelContent"></span>
                <div id="buttonWrapper" class="buttonWrapper">
                    <button id="selectedOption" class="selectedOption"></button>
                    <button class="toggleOptions">
                        <svg viewBox="0 0 32 32"><path d="M16.003 18.626l7.081-7.081L25 13.46l-8.997 8.998-9.003-9 1.917-1.916z"/></svg>
                    </button>
                </div><!--END buttonWrapper-->
                <div id="optionsWrapper" class="optionsWrapper">
                    <div id="optionsList" class="options">
                        <slot></slot>
                    </div>
                </div><!--END optionsWrapper-->
            </label>
        </div><!--END np-select-->
    `;

    class NpSelect extends HTMLElement {
        constructor() {
            /* Always call super first in constructor */
            super();

            /* bind 'this' to class in the method */
            this.toggleOptions = this.toggleOptions.bind(this);
            this.clickEvent = this.clickEvent.bind(this);
            this.selectedEvent = this.selectedEvent.bind(this);
            this.setSelectedOption = this.setSelectedOption.bind(this);


            /* Use the Shadow DOM */
            this.attachShadow({ mode: 'open' });
            this.shadowRoot.appendChild(template.content.cloneNode(true));

            /* Reference to DOM Elements */
            this.npSelect = this.shadowRoot.getElementById("np-select");
            this.npSelectHost = this.shadowRoot.host;
            this.btnWrapper = this.shadowRoot.getElementById("buttonWrapper");
            this.selectedBtn = this.shadowRoot.getElementById("selectedOption");
            this.optionsWrapper = this.shadowRoot.getElementById("optionsWrapper");
            this.optionsList = this.shadowRoot.getElementById("optionsList");
            this.labelWrapper = this.shadowRoot.getElementById("labelWrapper");
            this.labelContent = this.shadowRoot.getElementById("labelContent");
            this.errorLabel = this.shadowRoot.getElementById("errorLabel");


            /* Private Variables */
            this.selectedLabel="";
            this.value=null;
            this.layout="multiline";


        } /* END constructor */

        /* Called when added to DOM */
        connectedCallback() {

            if (!this.hasAttribute('state')) {
                this.setAttribute('state', "close");
            }

            /*** Set Event Listeners ***/

            /* Click event added to document to detect click outside of element */
            document.addEventListener('click', this.clickEvent);


            /* Listen for click event in np-select-option in slot */
            this.addEventListener( 'selected', this.selectedEvent);

        } /* END connectedCallback */

        /* Listen for when attributes change */
        /* attributes must be lowercase */
        static get observedAttributes() {
            return ['placeholder','state','selectedLabel','value','theme','label'];
        }

      /* Called when attributes change */
      attributeChangedCallback(name, oldValue, newValue) {

        if(name === 'placeholder') {
            this.selectedBtn.innerHTML=newValue;
            this.selectedBtn.classList.add('placeholder');
        }
        if(name === 'state') {
            if(newValue=='open'){
                this.npSelect.classList.add("open");

            }else if(newValue=='close'){
                this.npSelect.classList.remove("open");
            }
        }
        if(name ==='selectedLabel') {
            this.selectedBtn.innerHTML = newValue;
            this.selectedBtn.classList.remove('placeholder');
        }
        if(name === 'value') {
            // Making sure the child is selected if this attribute is set externally
            if(this.children.length > 0){
                for(var i=0; i < this.children.length; i++){
                    if(this.children[i].value === newValue){
                        // set the child to selected in not already set
                        if(!this.children[i].hasAttribute('selected')){
                            console.log('setting child '+this.children[i].value);
                            //this.children[i].setAttribute('selected','');
                        }
                    }else{
                        // remove 'selected' attribute from other children
                        this.children[i].removeAttribute('selected');
                    }
                }
                // Dispatch the event
                this.dispatchEvent(new CustomEvent('change',{bubbles:true,composed:true}));
            }
        }
        if(name === 'theme') {
            this.npSelect.classList.add(newValue);
        }
        if(name === 'label') {
            this.labelContent.innerHTML=newValue;
        }
       }/* END attributesChangedCallback */

      /* Getting attributes */
      get placeholder() {
        return this.getAttribute('placeholder');
      }
      get state() {
        return this.getAttribute('state');
      }
      get selectedLabel() {
          return this.getAttribute('selectedLabel');
      }
      get value() {
        return this.getAttribute('value');
    }
    get label() {
        return this.getAttribute('label');
    }

      /* Settinng attributes */
      set placeholder(newValue) {
        this.setAttribute('placeholder', newValue);
      }
      set state(newValue) {
        this.setAttribute('state', newValue);
      }
      set selectedLabel(newValue) {
          this.setAttribute('selectedLabel', newValue);
      }
      set value(newValue) {
          this.setAttribute('value', newValue);
      }
      set theme(newValue) {
        this.setAttribute('theme', newValue);
    }
    set theme(newValue) {
        this.setAttribute('theme', newValue);
    }
    set label(newValue) {
        this.setAttribute('label', newValue);
    }

      /* Called when removed from DOM */
      disconnectedCallback() {

        /*** Removing event listeners ***/

        /*Removing document click event */
        document.removeEventListener('click', this.clickEvent);

        /* Removing slot change event */
        this.shadowRoot.removeEventListener('slotchange', this.slotChangeEvent);

      }

        /*** Methods ***/
        /* Toggles the options menu */
        toggleOptions() {
           if(this.state == "close"){
               this.state="open";
           }else{
               this.state="close";
           }
        }

        /* Closes the options when clicked off */
        clickEvent(event) {

            /* Check that click target is np-select */
            if(this.npSelectHost == event.target){
                /* Clicked inside np-select - Toggle the options menu */
                this.toggleOptions();
            }else {
                /* Getting the dom tree of elements to the composedPath target */
                var foundInComposedPath = false;
                var foundElement=null;
                var composedPathArray = event.composedPath();

                /* Searching the tree of elements to see if this is in it */
                for(var i = 0; i < composedPathArray.length; i++){
                    if(composedPathArray[i] == this.npSelectHost){
                        foundInComposedPath = true;
                        foundElement = composedPathArray[i];
                    }
                }

                /* This is in composed Path - toggle the options menu */
                if(foundInComposedPath){
                    this.toggleOptions();
                }else{
                    /* Clicked outside np-select - Close the options menu */
                    this.state = "close";
                }
            }
        }

        /* Trigger when a option is selected */
        selectedEvent(event) {
            this.setSelectedOption(event.target);
        }

        /* Updates the selected option */
        setSelectedOption(npSelectOption) {
            /* Track the selected values */
            this.selectedLabel = npSelectOption.innerHTML;
            this.value = npSelectOption.getAttribute("value");

            /* Get all the options */
            var allOptions = npSelectOption.parentNode.getElementsByTagName("*");

            /* Remove 'hidden' attribute from all options */
            for(var i=0; i < allOptions.length; i++){
                allOptions[i].removeAttribute("hidden");
            }

            /* Hide the currently selected option in the options menu */
            npSelectOption.setAttribute("hidden","true");

            /* display the selected element on multiple lines */
            if(this.layout == 'multiline'){

                /* Splitting the name on two lines */
                if(this.selectedLabel.includes("\n")){
                    var strSplit = this.selectedLabel.split("\n");
                    this.selectedLabel = "<span class='top'>"+strSplit[0]+"</span><span class='btm'>"+strSplit[1]+"</span>";
                }
            }

            /* Display the selected option in the selected position */
            this.selectedBtn.innerHTML = this.selectedLabel;



        }

    } /* END class */

    window.customElements.define('np-select', NpSelect);

  })();
